/* A church's own signup questions, rendered.
 *
 * WHY THIS EXISTS. The console let a Super Admin define these — "How Many
 * Church Members", a number, required — the API served them on the signup
 * endpoint, and no client drew a single one. The field was saved, returned, and
 * silently ignored: the signup form typed the response as a narrower shape that
 * omitted custom_fields, so TypeScript dropped them before the form ever saw
 * them. Both callers then posted `field_values: {}`, so every answer was empty.
 *
 * ONE COMPONENT, TWO CALLERS. Somebody signing up through a church's link, and
 * somebody who joined the app first and is picking a church from the directory
 * later. Both ask the same questions, and answering them twice as differently
 * is how the two forms drift.
 *
 * The client's six types, from their handwritten flow:
 *   Text box · Check box · Drop down · Date time · Image · Number
 * Image is the one that cannot be a plain input — it needs an upload, which the
 * signup form has no session for. It renders as a note rather than pretending.
 */
import type { CustomField } from '@/types/api'

export interface CustomFieldInputsProps {
  fields: CustomField[]
  /** Answers so far, keyed by field id. */
  values: Record<string, string>
  onChange: (values: Record<string, string>) => void
  disabled?: boolean
}

/** What is still unanswered among the required ones. */
export function missingRequired(
  fields: CustomField[],
  values: Record<string, string>,
): CustomField[] {
  return fields.filter(
    (f) =>
      f.is_required &&
      f.field_type !== 'image' &&
      !String(values[f.id] ?? '').trim(),
  )
}

export function CustomFieldInputs({
  fields,
  values,
  onChange,
  disabled,
}: CustomFieldInputsProps) {
  if (!fields.length) return null

  function set(id: string, value: string) {
    onChange({ ...values, [id]: value })
  }

  return (
    <div className="space-y-s3">
      {[...fields]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((f) => {
          const value = values[f.id] ?? ''
          const label = (
            <span className="mb-1 block text-sm font-semibold text-ink-2">
              {f.label}
              {f.is_required ? (
                <span className="text-danger" aria-hidden>
                  {' '}
                  *
                </span>
              ) : null}
            </span>
          )

          const input = 'w-full rounded border border-line-2 bg-surface px-s3 py-s2 text-base outline-none focus:shadow-focus disabled:opacity-60'

          switch (f.field_type) {
            case 'check_box':
              return (
                <label
                  key={f.id}
                  className="flex cursor-pointer items-start gap-s2"
                >
                  <input
                    type="checkbox"
                    checked={value === 'true'}
                    disabled={disabled}
                    onChange={(e) => set(f.id, e.target.checked ? 'true' : '')}
                    className="mt-0.5 h-5 w-5 shrink-0 accent-[color:var(--cn-brand)]"
                  />
                  <span className="text-base">
                    {f.label}
                    {f.is_required ? (
                      <span className="text-danger" aria-hidden>
                        {' '}
                        *
                      </span>
                    ) : null}
                  </span>
                </label>
              )

            case 'drop_down':
              return (
                <label key={f.id} className="block">
                  {label}
                  <select
                    value={value}
                    required={f.is_required}
                    disabled={disabled}
                    onChange={(e) => set(f.id, e.target.value)}
                    className={input}
                  >
                    <option value="">Choose…</option>
                    {(f.options ?? []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
              )

            case 'image':
              // No session yet on the signup form, so no upload. Saying so
              // beats a control that cannot work.
              return (
                <div key={f.id}>
                  {label}
                  <p className="text-sm text-ink-2">
                    You can add this from your profile once you are signed in.
                  </p>
                </div>
              )

            default:
              return (
                <label key={f.id} className="block">
                  {label}
                  <input
                    type={
                      f.field_type === 'number'
                        ? 'number'
                        : f.field_type === 'date_time'
                          ? 'date'
                          : 'text'
                    }
                    value={value}
                    required={f.is_required}
                    disabled={disabled}
                    onChange={(e) => set(f.id, e.target.value)}
                    className={input}
                  />
                </label>
              )
          }
        })}
    </div>
  )
}
